// Tab "Contatos" do detalhe do cliente (story 2.2).
// - Lista contatos com mobile cards <768px (Tabela ≥768px)
// - Filtros: is_active (Ativos / Inativos / Todos), role
// - Botão "Novo contato" → dialog com ContactForm
// - Editar → dialog com ContactForm pré-preenchido
// - Desativar → DeactivateContactDialog (motivo obrigatório)

import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  Briefcase,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  PowerOff,
  PowerSquare,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { ContactRoleSchema, CreateClientContactDto } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { extractErrorMessage } from '../../lib/errors';
import type { ContactEntity } from '../../lib/contacts-api';
import { useContacts, useCreateContact, useUpdateContact } from '../../hooks/use-contacts';
import { PillButton } from '../clients/PillButton';
import { ContactForm } from './ContactForm';
import { DeactivateContactDialog } from './DeactivateContactDialog';

interface ContactsTabProps {
  clientId: string;
}

type Filter = 'active' | 'inactive' | 'all';

const ROLE_LABEL: Record<ContactRoleSchema, string> = {
  dono: 'Dono',
  socio: 'Sócio',
  gerente: 'Gerente',
  atendente: 'Atendente',
  financeiro: 'Financeiro',
  fiscal: 'Fiscal',
  comercial: 'Comercial',
  outro: 'Outro',
};

export function ContactsTab({ clientId }: ContactsTabProps): JSX.Element {
  const [filter, setFilter] = useState<Filter>('active');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContactEntity | null>(null);
  const [deactivating, setDeactivating] = useState<ContactEntity | null>(null);

  const queryFilters = useMemo(() => {
    if (filter === 'all') return {};
    return { is_active: filter === 'active' };
  }, [filter]);

  const list = useContacts(clientId, queryFilters);
  const create = useCreateContact(clientId);
  const update = useUpdateContact(clientId);

  function openNew(): void {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(contact: ContactEntity): void {
    setEditing(contact);
    setFormOpen(true);
  }

  async function handleSubmit(values: CreateClientContactDto): Promise<void> {
    try {
      if (editing) {
        await update.mutateAsync({ contactId: editing.id, payload: values });
        toast.success('Contato atualizado.');
      } else {
        await create.mutateAsync(values);
        toast.success('Contato criado.');
      }
      setFormOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos salvar o contato.'));
    }
  }

  async function handleDeactivate(reason: string): Promise<void> {
    if (!deactivating) return;
    try {
      await update.mutateAsync({
        contactId: deactivating.id,
        payload: { is_active: false, inactivated_reason: reason },
      });
      toast.success('Contato desativado.');
      setDeactivating(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos desativar o contato.'));
    }
  }

  async function handleReactivate(contact: ContactEntity): Promise<void> {
    try {
      await update.mutateAsync({
        contactId: contact.id,
        payload: { is_active: true },
      });
      toast.success('Contato reativado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos reativar o contato.'));
    }
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-[10px] border border-line p-[2px]">
          {(
            [
              { id: 'active', label: 'Ativos' },
              { id: 'inactive', label: 'Inativos' },
              { id: 'all', label: 'Todos' },
            ] as { id: Filter; label: string }[]
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFilter(opt.id)}
              aria-pressed={filter === opt.id}
              className={cn(
                'rounded-[6px] px-3 py-[5px] text-[12px] font-medium transition-colors',
                filter === opt.id ? 'bg-panel-3 text-ink' : 'text-muted hover:text-ink-2',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={openNew}>
          Novo contato
        </PillButton>
      </div>

      {/* Conteúdo */}
      {list.isLoading ? (
        <ContactsSkeleton />
      ) : list.isError ? (
        <ErrorBox
          onRetry={() => void list.refetch()}
          message={extractErrorMessage(list.error, 'Não conseguimos carregar os contatos.')}
        />
      ) : list.data && list.data.length > 0 ? (
        <ContactsList
          contacts={list.data}
          onEdit={openEdit}
          onDeactivate={(c) => setDeactivating(c)}
          onReactivate={(c) => void handleReactivate(c)}
        />
      ) : (
        <EmptyContacts onAdd={openNew} filter={filter} />
      )}

      {/* Dialog: criar / editar */}
      <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            )}
          />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
              'w-[min(560px,calc(100vw-32px))] max-h-[calc(100vh-48px)] overflow-y-auto',
              'rounded-[14px] border border-line bg-panel shadow-lg p-7',
              'focus:outline-none',
            )}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                {editing ? `Editar ${editing.name}` : 'Novo contato'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
                >
                  <X size={15} strokeWidth={2} />
                </button>
              </Dialog.Close>
            </div>

            <ContactForm
              defaultValues={editing ? toFormDefaults(editing) : undefined}
              submitLabel={editing ? 'Salvar alterações' : 'Adicionar contato'}
              isPending={create.isPending || update.isPending}
              onSubmit={handleSubmit}
              onCancel={() => setFormOpen(false)}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Dialog: desativar */}
      <DeactivateContactDialog
        open={Boolean(deactivating)}
        onOpenChange={(open) => {
          if (!open) setDeactivating(null);
        }}
        contactName={deactivating?.name ?? ''}
        isPending={update.isPending}
        onConfirm={(reason) => void handleDeactivate(reason)}
      />
    </div>
  );
}

// =============================================================================
// Subcomponentes
// =============================================================================

function ContactsList({
  contacts,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  contacts: ContactEntity[];
  onEdit: (c: ContactEntity) => void;
  onDeactivate: (c: ContactEntity) => void;
  onReactivate: (c: ContactEntity) => void;
}): JSX.Element {
  return (
    <>
      {/* Desktop: tabela */}
      <div className="hidden overflow-hidden rounded-[10px] border border-line md:block">
        <table className="w-full text-[13px]">
          <thead className="bg-panel-2 text-[11px] text-muted-2">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Nome</th>
              <th className="px-4 py-2 text-left font-medium">Função</th>
              <th className="px-4 py-2 text-left font-medium">Canais</th>
              <th className="px-4 py-2 text-left font-medium">Notificações</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-right font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-t border-line hover:bg-panel-2">
                <td className="px-4 py-3 text-ink-2">
                  <div className="font-medium text-ink">{c.name}</div>
                  {c.notes && (
                    <div className="mt-[2px] line-clamp-1 text-[11px] text-muted-2">{c.notes}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-muted">{ROLE_LABEL[c.role]}</td>
                <td className="px-4 py-3">
                  <ChannelIcons contact={c} />
                </td>
                <td className="px-4 py-3 text-[11px] text-muted-2">
                  <NotificationFlags contact={c} />
                </td>
                <td className="px-4 py-3">
                  <StatusDot isActive={c.is_active} />
                </td>
                <td className="px-4 py-3 text-right">
                  <RowActions
                    contact={c}
                    onEdit={onEdit}
                    onDeactivate={onDeactivate}
                    onReactivate={onReactivate}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {contacts.map((c) => (
          <article key={c.id} className="rounded-[10px] border border-line bg-panel-2 p-4">
            <header className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[14px] font-medium text-ink">{c.name}</div>
                <div className="text-[12px] text-muted">{ROLE_LABEL[c.role]}</div>
              </div>
              <StatusDot isActive={c.is_active} />
            </header>
            <div className="mt-3 flex items-center gap-3">
              <ChannelIcons contact={c} />
            </div>
            <div className="mt-3 text-[11px] text-muted-2">
              <NotificationFlags contact={c} />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <RowActions
                contact={c}
                onEdit={onEdit}
                onDeactivate={onDeactivate}
                onReactivate={onReactivate}
              />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function ChannelIcons({ contact }: { contact: ContactEntity }): JSX.Element {
  const channels: { icon: typeof Mail; label: string; value: string | null }[] = [
    { icon: Mail, label: 'Email', value: contact.email },
    { icon: Phone, label: 'Telefone', value: contact.phone },
    { icon: MessageCircle, label: 'WhatsApp', value: contact.whatsapp },
  ];
  return (
    <div className="flex items-center gap-2">
      {channels.map(({ icon: Icon, label, value }) =>
        value ? (
          <span
            key={label}
            title={`${label}: ${value}`}
            className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-panel-3 text-muted"
          >
            <Icon size={13} strokeWidth={1.8} aria-label={label} />
          </span>
        ) : null,
      )}
      {contact.preferred_channel && (
        <span className="text-[10px] uppercase tracking-wider text-muted-3">
          Pref. {contact.preferred_channel === 'ambos' ? 'ambos' : contact.preferred_channel}
        </span>
      )}
    </div>
  );
}

function NotificationFlags({ contact }: { contact: ContactEntity }): JSX.Element {
  const flags: string[] = [];
  if (contact.receives_billing_notifications) flags.push('Cobrança');
  if (contact.receives_fiscal_documents) flags.push('Fiscal');
  if (flags.length === 0) return <span className="text-muted-3">—</span>;
  return <span>{flags.join(' · ')}</span>;
}

function StatusDot({ isActive }: { isActive: boolean }): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-[6px] text-[11px] font-medium',
        isActive ? 'text-ink-2' : 'text-muted-2',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-[7px] w-[7px] rounded-full ring-[3px]',
          isActive ? 'bg-good ring-good/15' : 'bg-muted-2 ring-muted-3/15',
        )}
      />
      {isActive ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function RowActions({
  contact,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  contact: ContactEntity;
  onEdit: (c: ContactEntity) => void;
  onDeactivate: (c: ContactEntity) => void;
  onReactivate: (c: ContactEntity) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        aria-label={`Editar ${contact.name}`}
        onClick={() => onEdit(contact)}
        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
      >
        <Pencil size={13} strokeWidth={1.8} />
      </button>
      {contact.is_active ? (
        <button
          type="button"
          aria-label={`Desativar ${contact.name}`}
          onClick={() => onDeactivate(contact)}
          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad"
        >
          <PowerOff size={13} strokeWidth={1.8} />
        </button>
      ) : (
        <button
          type="button"
          aria-label={`Reativar ${contact.name}`}
          onClick={() => onReactivate(contact)}
          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-good"
        >
          <PowerSquare size={13} strokeWidth={1.8} />
        </button>
      )}
    </div>
  );
}

function ContactsSkeleton(): JSX.Element {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-14 animate-pulse rounded-[10px] border border-line bg-panel-2"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[10px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
      <div className="flex items-center gap-2 font-medium text-bad">
        <AlertCircle size={14} strokeWidth={2} aria-hidden="true" />
        Não conseguimos carregar os contatos.
      </div>
      <p className="mt-1 text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry}>Tentar de novo</PillButton>
      </div>
    </div>
  );
}

function EmptyContacts({ filter, onAdd }: { filter: Filter; onAdd: () => void }): JSX.Element {
  if (filter === 'inactive') {
    return (
      <div className="rounded-[10px] border border-line bg-panel-2 p-6 text-center">
        <p className="text-[13px] text-muted">Nenhum contato inativo.</p>
      </div>
    );
  }
  if (filter === 'all') {
    return (
      <div className="rounded-[10px] border border-line bg-panel-2 p-6 text-center">
        <div className="mb-2 flex justify-center text-muted-2">
          <Briefcase size={20} strokeWidth={1.5} aria-hidden="true" />
        </div>
        <p className="text-[13px] font-medium text-ink-2">Sem contatos ainda.</p>
        <p className="mt-1 text-[12px] text-muted">
          Cadastre quem responde por finanças, fiscal ou atendimento.
        </p>
        <div className="mt-4 flex justify-center">
          <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={onAdd}>
            Adicionar contato
          </PillButton>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-[10px] border border-line bg-panel-2 p-6 text-center">
      <div className="mb-2 flex justify-center text-muted-2">
        <MoreHorizontal size={20} strokeWidth={1.5} aria-hidden="true" />
      </div>
      <p className="text-[13px] font-medium text-ink-2">Sem contatos ativos.</p>
      <p className="mt-1 text-[12px] text-muted">
        Veja contatos inativos no filtro acima ou cadastre um novo.
      </p>
      <div className="mt-4 flex justify-center">
        <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={onAdd}>
          Adicionar contato
        </PillButton>
      </div>
    </div>
  );
}

// Converte ContactEntity → defaults do form (CreateClientContactInput-shape).
function toFormDefaults(c: ContactEntity): Partial<CreateClientContactDto> {
  return {
    name: c.name,
    role: c.role,
    email: c.email ?? '',
    phone: c.phone ?? '',
    whatsapp: c.whatsapp ?? '',
    notes: c.notes ?? '',
    is_active: c.is_active,
    preferred_channel: c.preferred_channel,
    receives_billing_notifications: c.receives_billing_notifications,
    receives_fiscal_documents: c.receives_fiscal_documents,
    inactivated_reason: c.inactivated_reason ?? null,
  };
}
